import { forwardRef } from "react";
import type { HTMLAttributes } from "react";

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "bordered" | "elevated";
  hover?: boolean;
  children: React.ReactNode;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { variant = "bordered", hover = false, className = "", children, ...props },
    ref
  ) => {
    const baseStyles = "bg-white text-black transition-all duration-300";
    const variants = {
      default: "border border-gray-200",
      bordered: "border-2 border-black",
      elevated: "border-2 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]",
    };

    const hoverStyles = hover
      ? "hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 cursor-pointer"
      : "";

    return (
      <div
        ref={ref}
        className={`${baseStyles} ${variants[variant]} ${hoverStyles} ${className}`}
        style={{ fontFamily: "'Courier New', monospace" }}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = "Card";

// CardHeader Component
interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  color?: "blue" | "purple" | "green" | "pink" | "yellow" | "orange" | "indigo";
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className = "", children, color = "blue", ...props }, ref) => {
    const colorClasses = {
      blue: "bg-blue-50/40",
      purple: "bg-purple-50/40",
      green: "bg-green-50/40",
      pink: "bg-pink-50/40",
      yellow: "bg-yellow-50/40",
      orange: "bg-orange-50/40",
      indigo: "bg-indigo-50/40",
    };

    return (
      <div
        ref={ref}
        className={`p-4 border-b-2 border-black ${colorClasses[color]} ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeader.displayName = "CardHeader";

// CardContent Component
interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className = "", children, ...props }, ref) => {
    return (
      <div ref={ref} className={`p-4 ${className}`} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = "CardContent";

// CardFooter Component
interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className = "", children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`p-4 border-t-2 border-black bg-gray-50 ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = "CardFooter";

export default Card;
export { Card, CardHeader, CardContent, CardFooter };
